import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { socioService } from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import PageContainer from "../../components/PageContainer";

const Socio = () => {
  const { showError, showSuccess } = useNotification();
  const confirmDialog = useConfirmDialog();

  const [loading, setLoading] = useState(false);
  const [socios, setSocios] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [paginacao, setPaginacao] = useState({ paginaAtual: 1, itensPorPagina: 6 });

  const [showForm, setShowForm] = useState(false);
  const [editingSocio, setEditingSocio] = useState(null);
  const [form, setForm] = useState({
    nome: "",
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadSocios();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSocios = async () => {
    setLoading(true);
    try {
      const data = await socioService.listar();
      setSocios(Array.isArray(data) ? data : []);
      setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
    } catch (err) {
      showError(err.message || "Erro ao carregar socios");
    } finally {
      setLoading(false);
    }
  };

  const sociosFiltrados = useMemo(() => {
    if (!searchTerm.trim()) {
      return socios;
    }

    return socios.filter((item) =>
      String(item?.nome || "").toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [socios, searchTerm]);

  const totalPaginas = Math.ceil(sociosFiltrados.length / paginacao.itensPorPagina);
  const indiceInicial = (paginacao.paginaAtual - 1) * paginacao.itensPorPagina;
  const sociosPaginados = sociosFiltrados.slice(
    indiceInicial,
    indiceInicial + paginacao.itensPorPagina
  );

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const handleNovo = () => {
    setEditingSocio(null);
    setForm({ nome: "" });
    setShowForm(true);
  };

  const handleEditar = (item) => {
    setEditingSocio(item);
    setForm({
      nome: item?.nome || "",
    });
    setShowForm(true);
  };

  const handleCancelar = () => {
    setShowForm(false);
    setEditingSocio(null);
    setForm({ nome: "" });
  };

  const handleSalvar = async () => {
    if (!form.nome.trim()) {
      showError("Nome do socio e obrigatorio");
      return;
    }

    const payload = {
      nome: form.nome.trim(),
    };

    setLoading(true);
    try {
      if (editingSocio?.id) {
        await socioService.atualizar(editingSocio.id, payload);
        showSuccess("Socio atualizado com sucesso!");
      } else {
        await socioService.cadastrar(payload);
        showSuccess("Socio cadastrado com sucesso!");
      }
      handleCancelar();
      await loadSocios();
    } catch (err) {
      showError(err.message || "Erro ao salvar socio");
    } finally {
      setLoading(false);
    }
  };

  const handleExcluir = async (id) => {
    const confirmed = await confirmDialog({
      title: "Confirmar",
      description: "Tem certeza que deseja excluir este socio?",
    });

    if (!confirmed) {
      return;
    }

    setLoading(true);
    try {
      await socioService.deletar(id);
      showSuccess("Socio excluido com sucesso!");
      await loadSocios();
    } catch (err) {
      showError(err.message || "Erro ao excluir socio");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Sócios</h1>
        <p className="text-gray-600">Gerencie os socios cadastrados no sistema</p>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between">
        <input
          type="text"
          placeholder="Pesquisar por nome..."
          value={searchTerm}
          onChange={handleSearch}
          className="w-full sm:max-w-md px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />

        <button
          onClick={handleNovo}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus size={18} />
          Novo Socio
        </button>
      </div>

      {showForm && (
        <div className="mb-6 p-6 bg-gray-50 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">
            {editingSocio ? "Editar Socio" : "Novo Socio"}
          </h3>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
              <input
                type="text"
                value={form.nome}
                onChange={(e) => setForm((prev) => ({ ...prev, nome: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Digite o nome do socio"
              />
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              onClick={handleSalvar}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-md"
            >
              Salvar
            </button>
            <button
              onClick={handleCancelar}
              disabled={loading}
              className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-md"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 text-left">Nome</th>
              <th className="p-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {sociosPaginados.length > 0 ? (
              sociosPaginados.map((item) => (
                <tr key={item.id} className="border-t hover:bg-gray-50 transition">
                  <td className="p-3">{item.nome || "-"}</td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleEditar(item)}
                        className="text-blue-600 hover:text-blue-900 disabled:text-gray-400 p-1 rounded transition-colors"
                        disabled={loading}
                        title="Editar"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => handleExcluir(item.id)}
                        className="text-red-600 hover:text-red-900 disabled:text-gray-400 p-1 rounded transition-colors"
                        disabled={loading}
                        title="Excluir"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="2" className="p-4 text-center text-gray-500">
                  {loading ? "Carregando..." : "Nenhum socio encontrado"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPaginas > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {Array.from({ length: totalPaginas }).map((_, index) => (
            <button
              key={index + 1}
              onClick={() => setPaginacao((prev) => ({ ...prev, paginaAtual: index + 1 }))}
              className={`px-3 py-1 rounded ${
                paginacao.paginaAtual === index + 1
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 hover:bg-gray-300"
              }`}
            >
              {index + 1}
            </button>
          ))}
        </div>
      )}
    </PageContainer>
  );
};

export default Socio;
